/**
 * Filter Configuration Types
 *
 * Domain-specific filter configuration system for URL state management.
 * Each domain defines its own filter config that handles serialization,
 * validation, and default values.
 *
 * @example
 * ```typescript
 * import { DomainFilterConfig, FilterFieldType } from '@spcc/core'
 *
 * export const salesOrdersFilterConfig: DomainFilterConfig<SalesOrdersFilter> = {
 *   domainName: 'salesOrders',
 *   fields: [
 *     { id: 'search', type: 'text', label: 'common.search', defaultValue: '' },
 *     { id: 'status', type: 'multi-select', label: 'salesOrders.filterByStatus', defaultValue: [] },
 *   ],
 *   defaults: DEFAULT_FILTER,
 *   serialize: (filter) => ({ search: filter.search }),
 *   deserialize: (params) => ({ search: params.search }),
 * }
 * ```
 */

import type { SortOrder } from '../strapi/query-builder'

// ============================================
// Filter Field Types
// ============================================

/**
 * Supported filter field types for UI rendering
 */
export type FilterFieldType =
  | 'text'           // Search/text input
  | 'select'         // Single select dropdown
  | 'multi-select'   // Multi-select (chips/checkboxes)
  | 'date-range'     // Date range picker (from/to)
  | 'number-range'   // Number range (min/max)
  | 'sort'           // Sort field + order combination

// ============================================
// Filter Field Configuration
// ============================================

/**
 * Base configuration for a single filter field
 */
export interface FilterFieldConfigBase {
  /** Unique field identifier (must match filter state key) */
  id: string
  /** Field type for UI rendering */
  type: FilterFieldType
  /** Display label (i18n key) */
  label: string
  /** Placeholder text (i18n key) */
  placeholder?: string
  /** Custom URL parameter name (defaults to field id) */
  urlParam?: string
  /** Exclude this field from URL serialization */
  excludeFromUrl?: boolean
  /** Whether field is required */
  required?: boolean
  /** Help text (i18n key) */
  helpText?: string
}

/**
 * Text field configuration
 */
export interface TextFilterFieldConfig extends FilterFieldConfigBase {
  type: 'text'
  defaultValue: string
  /** Minimum length */
  minLength?: number
  /** Maximum length */
  maxLength?: number
  /** Debounce delay in ms (for search inputs) */
  debounceMs?: number
}

/**
 * Option for select/multi-select fields
 */
export interface FilterSelectOption<T = string> {
  /** Value to use in filter state */
  value: T
  /** Display label (i18n key or string) */
  label: string
  /** Optional icon name */
  icon?: string
  /** Optional description */
  description?: string
  /** Option color (for status badges) */
  color?: string
}

/**
 * Single select field configuration
 */
export interface SelectFilterFieldConfig extends FilterFieldConfigBase {
  type: 'select'
  defaultValue: string
  /** Available options */
  options: FilterSelectOption[]
  /** Include "All" option at start */
  includeAllOption?: boolean
  /** Label for "All" option (i18n key) */
  allOptionLabel?: string
}

/**
 * Multi-select field configuration
 */
export interface MultiSelectFilterFieldConfig extends FilterFieldConfigBase {
  type: 'multi-select'
  defaultValue: string[]
  /** Available options (can be dynamic) */
  options?: FilterSelectOption[]
  /** Fetch options dynamically */
  fetchOptions?: () => Promise<FilterSelectOption[]>
  /** Allow search within options */
  searchable?: boolean
  /** Maximum number of selections */
  maxSelections?: number
  /** Show selected count badge */
  showSelectedCount?: boolean
}

/**
 * Date range field configuration
 */
export interface DateRangeFilterFieldConfig extends FilterFieldConfigBase {
  type: 'date-range'
  defaultValue: { from?: string; to?: string }
  /** Date format for display */
  dateFormat?: string
  /** Minimum selectable date */
  minDate?: string
  /** Maximum selectable date */
  maxDate?: string
  /** Preset ranges (e.g., "Last 7 days", "This month") */
  presets?: Array<{
    label: string
    from: string | (() => string)
    to: string | (() => string)
  }>
}

/**
 * Number range field configuration
 */
export interface NumberRangeFilterFieldConfig extends FilterFieldConfigBase {
  type: 'number-range'
  defaultValue: { min?: number; max?: number }
  /** Minimum allowed value */
  minValue?: number
  /** Maximum allowed value */
  maxValue?: number
  /** Step increment */
  step?: number
  /** Unit label (e.g., "SAR", "tons") */
  unit?: string
  /** Format function for display */
  formatValue?: (value: number) => string
}

/**
 * Sort configuration
 */
export interface SortFilterFieldConfig extends FilterFieldConfigBase {
  type: 'sort'
  defaultValue: { field: string; order: SortOrder }
  /** Available sort fields */
  sortFields: Array<{
    field: string
    label: string
    /** Whether this field supports both asc and desc */
    bidirectional?: boolean
  }>
}

/**
 * Union of all filter field configurations
 */
export type FilterFieldConfig =
  | TextFilterFieldConfig
  | SelectFilterFieldConfig
  | MultiSelectFilterFieldConfig
  | DateRangeFilterFieldConfig
  | NumberRangeFilterFieldConfig
  | SortFilterFieldConfig

// ============================================
// Domain Filter Configuration
// ============================================

/**
 * Complete domain filter configuration
 *
 * @template TFilter - The filter state type for this domain
 */
export interface DomainFilterConfig<TFilter extends Record<string, unknown>> {
  /** Domain name (used for namespacing) */
  domainName: string

  /** Field configurations for UI rendering */
  fields: FilterFieldConfig[]

  /** Default filter values */
  defaults: TFilter

  /**
   * Serialize filter state to URL params
   * Only include non-default values
   */
  serialize: (filter: TFilter) => Record<string, string>

  /**
   * Deserialize URL params to filter state
   * Returns partial filter to merge with defaults
   */
  deserialize: (params: Record<string, string | null>) => Partial<TFilter>

  /**
   * Validate filter values
   * Returns sanitized filter or throws error
   */
  validate?: (filter: Partial<TFilter>) => TFilter

  /**
   * URL param mapping (field id -> param name)
   * Defaults to using field id as param name
   */
  paramMapping?: Partial<Record<keyof TFilter, string>>
}

// ============================================
// Filter State Types
// ============================================

/**
 * Filter change handler
 */
export type FilterChangeHandler<TFilter> = (updates: Partial<TFilter>) => void

/**
 * Result from useUrlFilterSync hook
 */
export interface UseUrlFilterSyncResult<TFilter extends Record<string, unknown>> {
  /** Current filter state */
  filter: TFilter

  /** Update filter (merges with current state, updates URL) */
  setFilter: (updates: Partial<TFilter>) => void

  /** Reset to default values (clears URL params) */
  resetFilter: () => void

  /** Whether any filters differ from defaults */
  hasActiveFilters: boolean

  /** List of active filter keys */
  activeFilterKeys: Array<keyof TFilter>

  /** Count of active filters */
  activeFilterCount: number

  /** Generate shareable URL with current filters */
  getShareableUrl: () => string

  /** Generate URL search params */
  toSearchParams: () => URLSearchParams
}

// ============================================
// Filter Section Types (for FilterPanel)
// ============================================

/**
 * Section in the filter panel
 */
export interface FilterPanelSection {
  /** Unique section identifier */
  id: string
  /** Section title (i18n key) */
  title: string
  /** Section description (i18n key) */
  description?: string
  /** Whether section is collapsible */
  collapsible?: boolean
  /** Whether section starts collapsed */
  defaultCollapsed?: boolean
  /** Field IDs included in this section */
  fields: string[]
  /** Custom render function for section content */
  render?: () => React.ReactNode
}

// ============================================
// Helper Functions
// ============================================

/**
 * Check if a value is different from its default
 */
export function isActiveFilter(value: unknown, defaultValue: unknown): boolean {
  // Handle arrays
  if (Array.isArray(value) && Array.isArray(defaultValue)) {
    if (value.length !== defaultValue.length) return true
    return value.some((v, i) => v !== defaultValue[i])
  }

  // Handle objects (shallow comparison)
  if (typeof value === 'object' && value !== null && typeof defaultValue === 'object' && defaultValue !== null) {
    const valueKeys = Object.keys(value)
    const defaultKeys = Object.keys(defaultValue)
    if (valueKeys.length !== defaultKeys.length) return true
    return valueKeys.some((key) => (value as Record<string, unknown>)[key] !== (defaultValue as Record<string, unknown>)[key])
  }

  // Primitive comparison
  return value !== defaultValue
}

/**
 * Count active filters
 */
export function countActiveFilters<TFilter extends Record<string, unknown>>(
  filter: TFilter,
  defaults: TFilter
): number {
  let count = 0
  for (const key of Object.keys(defaults)) {
    if (isActiveFilter(filter[key], defaults[key])) {
      count += 1
    }
  }
  return count
}

/**
 * Get list of active filter keys
 */
export function getActiveFilterKeys<TFilter extends Record<string, unknown>>(
  filter: TFilter,
  defaults: TFilter
): Array<keyof TFilter> {
  const activeKeys: Array<keyof TFilter> = []
  for (const key of Object.keys(defaults)) {
    if (isActiveFilter(filter[key], defaults[key])) {
      activeKeys.push(key as keyof TFilter)
    }
  }
  return activeKeys
}

/**
 * Merge filter update with current state
 */
export function mergeFilterUpdate<TFilter extends Record<string, unknown>>(
  current: TFilter,
  updates: Partial<TFilter>
): TFilter {
  return { ...current, ...updates }
}

/**
 * Create a domain filter config with type inference
 */
export function createFilterConfig<TFilter extends Record<string, unknown>>(
  config: DomainFilterConfig<TFilter>
): DomainFilterConfig<TFilter> {
  return config
}
